import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Locale } from '@blauturquesa/shared/locales';
import type { PublicIndexCard } from '@blauturquesa/shared/public-view';
import { Screen } from '../../shell/Screen';
import { EmptyState } from '../../components/EmptyState';
import { Loading } from '../../components/Loading';
import { LazyMap } from '../map/LazyMap';
import type { MapPin } from '../map/MapView';
import { getPublicIndex } from './publicData';
import { pick, priceFromText } from './bits';

// Centre per defecte: les Illes Balears.
const DEFAULT_CENTER = { lat: 39.6, lng: 2.95 };
const esc = (s: string) => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);

/**
 * Pestanya «Mapa» (§13): totes les cases publicades, de `publicIndex/home` (una lectura). Cada punt és
 * l'aproximat, llevat de les cases que mostren la ubicació exacta. Les cases sense punt es compten (§10.1).
 */
export function MapPage() {
  const { t, i18n } = useTranslation(['public', 'shell']);
  const locale = i18n.language as Locale;
  const [cards, setCards] = useState<PublicIndexCard[] | null | undefined>(undefined);

  useEffect(() => {
    getPublicIndex().then((doc) => setCards(doc?.cards ?? []), () => setCards(null));
  }, []);

  if (cards === undefined) return <Screen title={t('shell:tabs.map')}><Loading /></Screen>;
  if (cards === null) return <Screen title={t('shell:tabs.map')}><EmptyState icon="globe" title={t('offline.title')}>{t('offline.body')}</EmptyState></Screen>;

  const placed = cards.filter((c) => c.location.point);
  if (placed.length === 0) return <Screen title={t('shell:tabs.map')}><EmptyState icon="map" title={t('shell:map.emptyTitle')}>{t('shell:map.emptyBody')}</EmptyState></Screen>;

  const pins: MapPin[] = placed.map((c) => {
    const title = pick(c.title, locale, c.sourceLocale).text;
    const place = [c.location.municipality, c.location.island].filter(Boolean).join(' · ');
    const price = priceFromText(c.priceFrom, c.modes, locale, t);
    return {
      id: c.propertyId,
      lat: c.location.point!.lat,
      lng: c.location.point!.lng,
      popupHtml: `<a class="map-popup-title" href="/p/${encodeURIComponent(c.slug)}">${esc(title)}</a><span>${esc(place)}</span>${price ? `<br><strong>${esc(price)}</strong>` : ''}`,
    };
  });
  const hidden = cards.length - placed.length;
  return (
    <Screen title={t('shell:tabs.map')} wide>
      <LazyMap className="map-full" label={t('map.allLabel')} center={placed.length === 1 ? placed[0]!.location.point! : DEFAULT_CENTER} zoom={placed.length === 1 ? 12 : 8} pins={pins} fitPins />
      <p className="muted small">{t('map.approxNote')}{hidden > 0 ? ` ${t('map.hidden', { count: hidden })}` : ''}</p>
    </Screen>
  );
}
